/**
 * Forge credit endpoints: the caller's balance and ledger, and the administrator's price table (TASK-0035).
 *
 * The balance endpoint answers the balance, the lifetime totals and the most recent movements in one response, so the
 * dashboard and the settings panel need exactly one request. The ledger is paged separately because a history can grow
 * without bound while the panel's summary must stay a fixed cost.
 *
 * Nothing here can refuse a call: no endpoint of this module answers `402`, and a negative balance travels as data
 * (`isNegative`) rather than as a problem document.
 */

import { apiClient } from '@/lib/apiClient';
import type {
  CreateProviderProfilePayload,
  ForgeBalance,
  ForgeLedgerPage,
  ForgePricing,
  ForgePrice,
  ProviderProfile,
  SelectableProviderProfile,
  UpdateProviderProfilePayload,
} from '@/types/api';

/** Largest page the ledger endpoint serves; a larger `take` is clamped server-side. */
export const FORGE_LEDGER_MAX_PAGE_SIZE = 100;

/** Default page size of the ledger endpoint, and the one the history panel asks for. */
export const FORGE_LEDGER_DEFAULT_PAGE_SIZE = 25;

/**
 * Body of `PUT /admin/forge/pricing`; the table is replaced whole.
 *
 * A replacement rather than a patch: an empty list clears every override and falls back to the configured prices, which is
 * how an administrator undoes a change. A row's `providerProfileId` is what ties its price to the connection that serves the
 * call (ADR-0019 §D5); `null` is the global override.
 */
export interface UpdateForgePricingPayload {
  /** Complete new override table. */
  models: ForgePrice[];
}

/** Forge credit operations of the signed-in account. */
export const forgeApi = {
  /** Reads the caller's balance, lifetime usage and most recent movements. */
  async getBalance(): Promise<ForgeBalance> {
    const { data } = await apiClient.get<ForgeBalance>('/users/me/forge');
    return data;
  },

  /** Reads one page of the caller's ledger, newest first. */
  async getLedger(skip = 0, take = FORGE_LEDGER_DEFAULT_PAGE_SIZE): Promise<ForgeLedgerPage> {
    const { data } = await apiClient.get<ForgeLedgerPage>('/users/me/forge/ledger', {
      params: { skip, take },
    });
    return data;
  },
};

/** Administrative Forge operations; every call is role-gated on the API side. */
export const adminForgeApi = {
  /** Reads the effective price table: overrides, configured prices and the fallback. */
  async getPricing(): Promise<ForgePricing> {
    const { data } = await apiClient.get<ForgePricing>('/admin/forge/pricing');
    return data;
  },

  /** Replaces the override table and returns the table the next charge will use. */
  async updatePricing(payload: UpdateForgePricingPayload): Promise<ForgePricing> {
    const { data } = await apiClient.put<ForgePricing>('/admin/forge/pricing', payload);
    return data;
  },
};

/**
 * Administrator-managed AI provider profiles (ADR-0019).
 *
 * The administrative surface, gated on the API side by the administrator policy and re-checked inside every use case. No
 * endpoint of this module returns a secret: what identifies a stored credential is the fingerprint the vault derived from it.
 */
export const adminProviderProfileApi = {
  /** Lists every profile, optionally including the soft-deleted ones. */
  async list(includeDeleted = false): Promise<ProviderProfile[]> {
    const { data } = await apiClient.get<ProviderProfile[]>('/admin/provider-profiles', {
      params: { includeDeleted },
    });
    return data;
  },

  /** Creates a profile and protects the submitted key with the platform scope. */
  async create(payload: CreateProviderProfilePayload): Promise<ProviderProfile> {
    const { data } = await apiClient.post<ProviderProfile>('/admin/provider-profiles', payload);
    return data;
  },

  /** Edits a profile; an omitted `apiKey` keeps the stored credential. */
  async update(profileId: string, payload: UpdateProviderProfilePayload): Promise<ProviderProfile> {
    const { data } = await apiClient.put<ProviderProfile>(
      `/admin/provider-profiles/${profileId}`,
      payload,
    );
    return data;
  },

  /** Enables or disables a profile without touching its credential. */
  async setState(profileId: string, isActive: boolean): Promise<ProviderProfile> {
    const { data } = await apiClient.post<ProviderProfile>(
      `/admin/provider-profiles/${profileId}/state`,
      {
        isActive,
      },
    );
    return data;
  },

  /** Soft-deletes a profile, so its pricing and audit references keep resolving. */
  async remove(profileId: string): Promise<ProviderProfile> {
    const { data } = await apiClient.delete<ProviderProfile>(
      `/admin/provider-profiles/${profileId}`,
    );
    return data;
  },
};

/** Platform provider profiles a user may select (ADR-0019 §D3). */
export const providerProfileApi = {
  /** Lists the active, visible profiles; the list is filtered server-side. */
  async listSelectable(): Promise<SelectableProviderProfile[]> {
    const { data } = await apiClient.get<SelectableProviderProfile[]>('/ai/provider-profiles');
    return data;
  },
};
